import {
  Alert,
  AlertDescription,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { OverviewTab } from '@client/features/wizard-advisors-metrics/components/overview-tab';
import { AlertCircle } from 'lucide-react';
import { AdvisorsMetricsTable } from '@client/features/wizard-advisors-metrics/components/advisors-metrics-table';
import { useSearchParams } from 'react-router-dom';
import { UsageAnalyticsView } from '../../../pages/AdvisorUsageAnalytics';
import {
  filterAdvisorsByPeriodActivity,
  isPeriodActivityFilter,
  useAdvisorMetrics,
} from '../hooks/use-advisor-metrics';
import { useAdvisorUsageMetrics } from '../hooks/use-advisor-usage-metrics';
import { SubscriptionDetailsTable } from './subscription-details-table';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useEffect, useMemo, useState } from 'react';
import type { MetricsOrganization } from '../types/metrics-type';
import { useNylMetricsApi, useNylMetricsExport } from '../hooks/api/use-advisor-metrics-api';
import { getValidDateRangeParams, useUsageDateFilter } from '../hooks/use-date-filter';
import { useMetricFilterParams } from '../hooks/use-metric-filter-params';
import { endOfDay, format, parse, startOfDay } from 'date-fns';
import { EnhancedAdvisorUsageTable } from './enhanced-advisor-usage-table';

export function AdvisorMetricsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const { filter, usageFilter } = useMetricFilterParams(activeTab);
  const routePeriodFrom = searchParams.get('from') ?? undefined;
  const routePeriodTo = searchParams.get('to') ?? undefined;
  const routeOrganization: MetricsOrganization =
    searchParams.get('partner') === 'nyl' ? 'nyl' : 'all';
  const { flags } = useFeatureFlags();
  const [overviewOrganization, setOverviewOrganization] = useState<MetricsOrganization>(() =>
    activeTab === 'overview' ? routeOrganization : 'all',
  );
  const [advisorsOrganization, setAdvisorsOrganization] = useState<MetricsOrganization>(() =>
    activeTab === 'advisors' ? routeOrganization : 'all',
  );
  const [subscriptionsOrganization, setSubscriptionsOrganization] = useState<MetricsOrganization>(
    () => (activeTab === 'subscriptions' ? routeOrganization : 'all'),
  );
  const [usageOrganization, setUsageOrganization] = useState<MetricsOrganization>(() =>
    activeTab === 'usage' ? routeOrganization : 'all',
  );

  useEffect(() => {
    if (activeTab === 'overview') setOverviewOrganization(routeOrganization);
    if (activeTab === 'advisors') setAdvisorsOrganization(routeOrganization);
    if (activeTab === 'subscriptions') setSubscriptionsOrganization(routeOrganization);
    if (activeTab === 'usage') setUsageOrganization(routeOrganization);
  }, [activeTab, routeOrganization]);
  const metrics = useAdvisorMetrics(overviewOrganization, activeTab === 'overview');
  const advisorsMetricsModel = useAdvisorMetrics(advisorsOrganization, activeTab === 'advisors');
  const subscriptionsMetricsModel = useAdvisorMetrics(
    subscriptionsOrganization,
    activeTab === 'subscriptions',
  );
  const usageMetricsModel = useAdvisorMetrics(usageOrganization, activeTab === 'usage');
  const metricsModelsByTab: Record<string, typeof metrics> = {
    overview: metrics,
    advisors: advisorsMetricsModel,
    subscriptions: subscriptionsMetricsModel,
    usage: usageMetricsModel,
  };
  const activeMetricsModel = metricsModelsByTab[activeTab] ?? metrics;
  const {
    data: {
      misc: { isLoading, isFetching, error },
    },
  } = activeMetricsModel;
  const { getFilteredAdvisors: getBaseFilteredAdvisorsForAdvisorsTab } =
    advisorsMetricsModel.actions;
  const { getFilteredSubscriptions: getFilteredSubscriptionsForSubscriptionsTab } =
    subscriptionsMetricsModel.actions;
  const {
    data: { usageMetrics },
    actions: { getFilteredAdvisorsForUsage },
  } = usageMetricsModel;
  const { dateFilter: overviewDateFilter } = metrics.data.misc;
  const usageDateFilter = useUsageDateFilter(activeTab === 'usage');
  const nylMetricsEnabled = flags.nylMetrics === true;
  const loadNylOverviewMetrics = nylMetricsEnabled && activeTab === 'overview';
  const loadNylUsageMetrics =
    nylMetricsEnabled && activeTab === 'usage' && !usageDateFilter.isHydrating;
  const usageMetricsRange =
    usageDateFilter.dateFilter.preset === 'allTime'
      ? {}
      : {
          from: format(usageDateFilter.dateFilter.startDate, 'yyyy-MM-dd'),
          to: format(usageDateFilter.dateFilter.endDate, 'yyyy-MM-dd'),
        };
  const overviewNylMetricsQuery = useNylMetricsApi({
    enabled: loadNylOverviewMetrics,
    scope: overviewOrganization,
    staleTimeMs: 0,
    ...(overviewDateFilter.preset === 'allTime'
      ? {}
      : {
          from: format(overviewDateFilter.startDate, 'yyyy-MM-dd'),
          to: format(overviewDateFilter.endDate, 'yyyy-MM-dd'),
        }),
  });
  const overviewRangeContext = `${overviewDateFilter.preset}:${format(
    overviewDateFilter.startDate,
    'yyyy-MM-dd',
  )}:${format(overviewDateFilter.endDate, 'yyyy-MM-dd')}`;
  const overviewNylAllTimeMetricsQuery = useNylMetricsApi({
    enabled: loadNylOverviewMetrics && overviewDateFilter.preset !== 'allTime',
    scope: overviewOrganization,
    staleTimeMs: 0,
    retainPreviousData: true,
    queryKeyContext: `overview-comparison:${overviewRangeContext}`,
  });
  const overviewNylAllTimeMetrics =
    overviewDateFilter.preset === 'allTime'
      ? overviewNylMetricsQuery.data
      : overviewNylAllTimeMetricsQuery.data;
  const usageNylMetricsQuery = useNylMetricsApi({
    enabled: loadNylUsageMetrics,
    scope: usageOrganization,
    ...usageMetricsRange,
  });
  const advisorPeriodFilter = isPeriodActivityFilter(filter) ? filter : null;
  const routePeriodRange = useMemo(
    () => getValidDateRangeParams(routePeriodFrom, routePeriodTo),
    [routePeriodFrom, routePeriodTo],
  );
  const hasInvalidRoutePeriod =
    Boolean(routePeriodFrom || routePeriodTo) && routePeriodRange === undefined;
  useEffect(() => {
    if (activeTab !== 'advisors' && activeTab !== 'subscriptions') return;

    const targetModel = activeTab === 'advisors' ? advisorsMetricsModel : subscriptionsMetricsModel;
    if (activeTab === 'advisors' && advisorPeriodFilter !== null) {
      targetModel.actions.resetFilter();
      return;
    }
    if (!routePeriodRange) {
      targetModel.actions.resetFilter();
      return;
    }

    targetModel.actions.setCustomDates(
      startOfDay(parse(routePeriodRange.from, 'yyyy-MM-dd', new Date())),
      endOfDay(parse(routePeriodRange.to, 'yyyy-MM-dd', new Date())),
    );
  }, [
    activeTab,
    advisorPeriodFilter,
    advisorsMetricsModel.actions.resetFilter,
    advisorsMetricsModel.actions.setCustomDates,
    routePeriodRange,
    subscriptionsMetricsModel.actions.resetFilter,
    subscriptionsMetricsModel.actions.setCustomDates,
  ]);
  const advisorsPeriodMetricsQuery = useNylMetricsApi({
    enabled:
      nylMetricsEnabled &&
      activeTab === 'advisors' &&
      advisorPeriodFilter !== null &&
      !hasInvalidRoutePeriod,
    scope: advisorsOrganization,
    ...routePeriodRange,
  });
  const advisorsTableAdvisors = useMemo(() => {
    if (!nylMetricsEnabled || advisorPeriodFilter === null) {
      return getBaseFilteredAdvisorsForAdvisorsTab(filter);
    }
    if (hasInvalidRoutePeriod || !advisorsPeriodMetricsQuery.data) return [];
    return filterAdvisorsByPeriodActivity(
      advisorsMetricsModel.data.advisorMetrics.activeAccessAdvisors,
      advisorsPeriodMetricsQuery.data.agents,
      advisorPeriodFilter,
    );
  }, [
    advisorPeriodFilter,
    advisorsMetricsModel.data.advisorMetrics.activeAccessAdvisors,
    advisorsPeriodMetricsQuery.data,
    filter,
    getBaseFilteredAdvisorsForAdvisorsTab,
    hasInvalidRoutePeriod,
    nylMetricsEnabled,
  ]);
  const refreshOverviewMetrics = () => {
    void metrics.actions.refetch();
    if (!loadNylOverviewMetrics) return;

    void overviewNylMetricsQuery.refetch();
    if (overviewDateFilter.preset !== 'allTime') {
      void overviewNylAllTimeMetricsQuery.refetch();
    }
  };
  const refreshUsageMetrics = () => {
    void usageMetricsModel.actions.refetch();
    if (loadNylUsageMetrics) void usageNylMetricsQuery.refetch();
  };
  const nylMetricsExport = useNylMetricsExport(
    nylMetricsEnabled &&
      activeTab === 'usage' &&
      usageOrganization === 'nyl' &&
      !usageDateFilter.isHydrating,
    {
      scope: 'nyl',
      ...usageMetricsRange,
    },
  );
  const usagePeriodFilter = isPeriodActivityFilter(usageFilter) ? usageFilter : null;
  const usageFilteredAdvisors = useMemo(() => {
    if (usageDateFilter.isHydrating) return [];
    if (!nylMetricsEnabled || usagePeriodFilter === null) {
      return getFilteredAdvisorsForUsage(usageFilter);
    }
    if (!usageNylMetricsQuery.data) return [];
    return filterAdvisorsByPeriodActivity(
      usageMetricsModel.data.advisorMetrics.activeAccessAdvisors,
      usageNylMetricsQuery.data.agents,
      usagePeriodFilter,
    );
  }, [
    getFilteredAdvisorsForUsage,
    nylMetricsEnabled,
    usageDateFilter.isHydrating,
    usageFilter,
    usageMetricsModel.data.advisorMetrics.activeAccessAdvisors,
    usageNylMetricsQuery.data,
    usagePeriodFilter,
  ]);
  const { advisorsList, activeAdvisorsCount, calculatedMetrics } = useAdvisorUsageMetrics(
    usageMetrics?.usageDetails || {},
    usageFilteredAdvisors,
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load overview metrics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <div className="mt-8 flex flex-col items-center justify-center gap-6">
          <LoadingSpinner size={60} />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Advisor Metrics"
        description="Comprehensive analytics and insights across all advisors"
        className="items-start md:items-center"
      />

      {((loadNylOverviewMetrics &&
        (overviewNylMetricsQuery.error || overviewNylAllTimeMetricsQuery.error)) ||
        (loadNylUsageMetrics && usageNylMetricsQuery.error) ||
        (advisorPeriodFilter !== null &&
          (hasInvalidRoutePeriod || advisorsPeriodMetricsQuery.error))) && (
        <Alert
          variant="destructive"
          className="mt-6"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load NYL metrics. Existing Wizard metrics remain available.
          </AlertDescription>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onValueChange={value => {
          const organizationsByTab: Record<string, MetricsOrganization> = {
            overview: overviewOrganization,
            advisors: advisorsOrganization,
            subscriptions: subscriptionsOrganization,
            usage: usageOrganization,
          };
          const organization = organizationsByTab[value] ?? 'all';
          setSearchParams({
            tab: value,
            ...(organization === 'nyl' ? { partner: 'nyl' } : {}),
          });
        }}
        className="mt-8"
      >
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4 lg:w-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="advisors">Advisors</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
        </TabsList>

        <TabsContent
          value="overview"
          className="mt-6"
        >
          <OverviewTab
            metrics={metrics}
            nylMetrics={nylMetricsEnabled ? overviewNylMetricsQuery.data : undefined}
            nylAllTimeMetrics={nylMetricsEnabled ? overviewNylAllTimeMetrics : undefined}
            showNylMetrics={nylMetricsEnabled}
            organization={nylMetricsEnabled ? overviewOrganization : undefined}
            onOrganizationChange={
              nylMetricsEnabled
                ? organization => {
                    setOverviewOrganization(organization);
                    const nextParams = new URLSearchParams(searchParams);
                    if (organization === 'nyl') nextParams.set('partner', 'nyl');
                    else nextParams.delete('partner');
                    setSearchParams(nextParams);
                  }
                : undefined
            }
            onRefresh={refreshOverviewMetrics}
            isRefreshing={
              isFetching ||
              (loadNylOverviewMetrics &&
                (overviewNylMetricsQuery.isFetching ||
                  (overviewDateFilter.preset !== 'allTime' &&
                    overviewNylAllTimeMetricsQuery.isFetching)))
            }
            onMetricClick={(tab: string, filterKey: string) => {
              if (tab === 'advisors') {
                setAdvisorsOrganization(overviewOrganization);
                if (overviewDateFilter.preset === 'allTime') {
                  advisorsMetricsModel.actions.resetFilter();
                } else {
                  advisorsMetricsModel.actions.setCustomDates(
                    overviewDateFilter.startDate,
                    overviewDateFilter.endDate,
                  );
                }
              }
              if (tab === 'subscriptions') {
                setSubscriptionsOrganization(overviewOrganization);
                if (overviewDateFilter.preset === 'allTime') {
                  subscriptionsMetricsModel.actions.resetFilter();
                } else {
                  subscriptionsMetricsModel.actions.setCustomDates(
                    overviewDateFilter.startDate,
                    overviewDateFilter.endDate,
                  );
                }
              }
              setSearchParams({
                tab,
                ...(filterKey === 'all' ? {} : { filter: filterKey }),
                ...(overviewOrganization === 'nyl' ? { partner: 'nyl' } : {}),
                ...(overviewDateFilter.preset !== 'allTime'
                  ? {
                      from: format(overviewDateFilter.startDate, 'yyyy-MM-dd'),
                      to: format(overviewDateFilter.endDate, 'yyyy-MM-dd'),
                    }
                  : {}),
              });
            }}
          />
        </TabsContent>

        <TabsContent
          value="advisors"
          className="mt-6"
        >
          <AdvisorsMetricsTable
            advisors={advisorsTableAdvisors}
            isLoading={
              isLoading ||
              (advisorPeriodFilter !== null &&
                !hasInvalidRoutePeriod &&
                advisorsPeriodMetricsQuery.isLoading)
            }
            filterKey={filter}
            organization={nylMetricsEnabled ? advisorsOrganization : undefined}
            onOrganizationChange={nylMetricsEnabled ? setAdvisorsOrganization : undefined}
            onRefresh={() => {
              void advisorsMetricsModel.actions.refetch();
              if (advisorPeriodFilter !== null && !hasInvalidRoutePeriod) {
                void advisorsPeriodMetricsQuery.refetch();
              }
            }}
            isRefreshing={
              advisorsMetricsModel.data.misc.isFetching ||
              (advisorPeriodFilter !== null && advisorsPeriodMetricsQuery.isFetching)
            }
            onCheckUsage={(filterKey, organization) => {
              setUsageOrganization(organization);
              setSearchParams({
                tab: 'usage',
                ...(filterKey && filterKey !== 'all' ? { usageFilter: filterKey } : {}),
                ...(organization === 'nyl' ? { partner: 'nyl' } : {}),
                ...routePeriodRange,
              });
            }}
          />
        </TabsContent>

        <TabsContent
          value="subscriptions"
          className="mt-6"
        >
          <SubscriptionDetailsTable
            getFilteredSubscriptions={getFilteredSubscriptionsForSubscriptionsTab}
            isLoading={isLoading}
            filterKey={filter}
            organization={nylMetricsEnabled ? subscriptionsOrganization : undefined}
            onOrganizationChange={nylMetricsEnabled ? setSubscriptionsOrganization : undefined}
            onRefresh={() => void subscriptionsMetricsModel.actions.refetch()}
            isRefreshing={subscriptionsMetricsModel.data.misc.isFetching}
            onCheckUsage={(filterKey, organization) => {
              setUsageOrganization(organization);
              setSearchParams({
                tab: 'usage',
                ...(filterKey && filterKey !== 'all' ? { usageFilter: filterKey } : {}),
                ...(organization === 'nyl' ? { partner: 'nyl' } : {}),
              });
            }}
          />
        </TabsContent>

        <TabsContent
          value="usage"
          className="mt-6"
        >
          {nylMetricsEnabled ? (
            <EnhancedAdvisorUsageTable
              advisors={advisorsList}
              onRefresh={refreshUsageMetrics}
              isRefreshing={
                usageMetricsModel.data.misc.isFetching || usageNylMetricsQuery.isFetching
              }
              nylMetrics={usageNylMetricsQuery.data}
              isNylMetricsLoading={usageDateFilter.isHydrating || usageNylMetricsQuery.isLoading}
              dateFilter={usageDateFilter.dateFilter}
              organization={usageOrganization}
              onOrganizationChange={organization => {
                setUsageOrganization(organization);
                const nextParams = new URLSearchParams(searchParams);
                if (organization === 'nyl') nextParams.set('partner', 'nyl');
                else nextParams.delete('partner');
                setSearchParams(nextParams);
              }}
              onDatePresetChange={usageDateFilter.setPreset}
              onCustomDatesChange={usageDateFilter.setCustomDates}
              onExportNylClients={nylMetricsExport.startExport}
              isNylExporting={nylMetricsExport.isExporting}
              calculatedMetrics={
                usageDateFilter.isHydrating ||
                (usagePeriodFilter !== null && !usageNylMetricsQuery.data)
                  ? undefined
                  : calculatedMetrics
              }
              filterKey={usageFilter}
              onClearFilter={() => {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('usageFilter');
                setSearchParams(nextParams);
              }}
            />
          ) : (
            <UsageAnalyticsView
              activeAdvisorsCount={activeAdvisorsCount}
              isLoading={isLoading || usageMetricsModel.data.misc.isFetching}
              calculatedMetrics={calculatedMetrics}
              advisorsList={advisorsList}
              refetch={refreshUsageMetrics}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
